import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { AlertCircle, Loader2, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  reprocessDocIntelligenceUpload,
  uploadDocIntelligenceFile
} from '../../services/api/implementation/impl';
import DocIntelApplyPanel from './DocIntelApplyPanel';
import DocIntelMappingPanel from './DocIntelMappingPanel';
import DocIntelReviewPanel from './DocIntelReviewPanel';
import DocIntelSidebar from './DocIntelSidebar';
import DocIntelTemplatePanel from './DocIntelTemplatePanel';
import DocIntelUploadPanel from './DocIntelUploadPanel';
import {
  btnPrimary,
  DocIntelMainCard,
  DocIntelPhaseRail,
  DocIntelStatePanel
} from './DocIntelUi';
import { getPanelForUpload, getPanelMeta } from './docIntelUtils';
import { useDocIntelUploads } from './useDocIntelUploads';

const DocIntelWorkspace = ({ dealId }) => {
  const {
    uploads,
    selectedUpload,
    selectedUploadId,
    reviewSessionId,
    selectUpload,
    invalidateAll,
    isLoading
  } = useDocIntelUploads(dealId);

  const [applyResult, setApplyResult] = useState(null);
  const [uploadingCount, setUploadingCount] = useState(0);
  const [templateStudioOpen, setTemplateStudioOpen] = useState(false);
  const [templateBySession, setTemplateBySession] = useState({});

  const uploadMutation = useMutation({
    mutationFn: async (files) => {
      setUploadingCount(files.length);
      const results = [];
      for (const file of files) {
        results.push(
          await uploadDocIntelligenceFile(dealId, file, { source_type: 'deal_attachment' })
        );
      }
      return results;
    },
    onSuccess: (results) => {
      toast.success(
        results.length === 1 ? 'Upload started' : `${results.length} files queued`
      );
      const last = results[results.length - 1];
      const newId = last?.uploadId ?? last?.id;
      if (newId) selectUpload(newId);
      invalidateAll();
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || err?.message || 'Upload failed');
    },
    onSettled: () => setUploadingCount(0)
  });

  const reprocessMutation = useMutation({
    mutationFn: (uploadId) => reprocessDocIntelligenceUpload(uploadId ?? selectedUploadId),
    onSuccess: (_data, uploadId) => {
      if (uploadId) selectUpload(uploadId);
      toast.success('Retry queued');
      invalidateAll();
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || err?.message || 'Retry failed');
    }
  });

  const panel = selectedUpload ? getPanelForUpload(selectedUpload) : 'empty';
  const displayPanel = templateStudioOpen && panel === 'success' ? 'template' : panel;
  const meta = getPanelMeta(displayPanel, selectedUpload);
  const isUploading = uploadMutation.isPending || uploadingCount > 0;
  const templateIdForSession = reviewSessionId ? templateBySession[reviewSessionId] : null;

  const wrapWithShell = (content, { includeTemplate = false } = {}) => {
    if (panel === 'empty' || !uploads.length) return content;
    return (
      <DocIntelMainCard className="flex min-h-[360px] flex-col">
        <DocIntelPhaseRail panel={displayPanel} includeTemplate={includeTemplate} />
        <div className="min-h-0 flex-1">{content}</div>
      </DocIntelMainCard>
    );
  };

  const renderMainPanel = () => {
    if (isLoading && !uploads.length) {
      return (
        <DocIntelStatePanel
          tone="processing"
          icon={Loader2}
          title="Loading documents…"
          description="Fetching smart uploads for this deal."
        />
      );
    }

    if (!uploads.length) {
      return <DocIntelUploadPanel onUploadFiles={(f) => uploadMutation.mutate(f)} isUploading={isUploading} />;
    }

    switch (panel) {
      case 'processing':
        return wrapWithShell(
          <DocIntelStatePanel
            embedded
            tone="processing"
            icon={Loader2}
            spinIcon
            title={meta.title}
            description={meta.subtitle}
            action={<p className="max-w-xs text-xs leading-relaxed text-slate-500">{meta.hint}</p>}
          />
        );

      case 'failed':
        return wrapWithShell(
          <DocIntelStatePanel
            embedded
            tone="error"
            icon={AlertCircle}
            title={meta.title}
            description={meta.hint}
            action={
              <button
                type="button"
                disabled={reprocessMutation.isPending}
                onClick={() => reprocessMutation.mutate(selectedUploadId)}
                className={btnPrimary}>
                {reprocessMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
                Retry extraction
              </button>
            }
          />
        );

      case 'review':
        return wrapWithShell(
          <DocIntelReviewPanel
            upload={selectedUpload}
            reviewSessionId={reviewSessionId}
            meta={meta}
            onUpdated={() => {
              setApplyResult(null);
              invalidateAll();
            }}
          />
        );

      case 'mapping':
        return wrapWithShell(
          <DocIntelMappingPanel
            reviewSessionId={reviewSessionId}
            meta={meta}
            onApplied={(result) => {
              setApplyResult(result);
              invalidateAll();
            }}
          />
        );

      case 'success':
        if (templateStudioOpen) {
          return wrapWithShell(
            <DocIntelTemplatePanel
              dealId={dealId}
              reviewSessionId={reviewSessionId}
              meta={meta}
              templateId={templateIdForSession}
              onTemplateCreated={(id) => {
                if (reviewSessionId) {
                  setTemplateBySession((prev) => ({ ...prev, [reviewSessionId]: id }));
                }
              }}
              onBack={() => setTemplateStudioOpen(false)}
            />,
            { includeTemplate: true }
          );
        }
        return wrapWithShell(
          <DocIntelApplyPanel
            dealId={dealId}
            meta={meta}
            applyResult={applyResult ?? selectedUpload?.apply_summary ?? null}
            onOpenTemplateStudio={() => setTemplateStudioOpen(true)}
          />
        );

      default:
        return <DocIntelUploadPanel onUploadFiles={(f) => uploadMutation.mutate(f)} isUploading={isUploading} />;
    }
  };

  return (
    <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
      {uploads.length > 0 && (
        <DocIntelSidebar
          uploads={uploads}
          selectedUploadId={selectedUploadId}
          onSelect={(id) => {
            setApplyResult(null);
            setTemplateStudioOpen(false);
            selectUpload(id);
          }}
          onUploadFiles={(f) => uploadMutation.mutate(f)}
          onRetryUpload={(id) => reprocessMutation.mutate(id)}
          retryingUploadId={reprocessMutation.isPending ? reprocessMutation.variables : null}
          isUploading={isUploading}
        />
      )}

      <div className="min-w-0 flex-1">{renderMainPanel()}</div>
    </div>
  );
};

export default DocIntelWorkspace;
